import { createContext, FC, ReactNode, RefObject, useMemo, useRef, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom-v5-compat';
import styled, { css } from 'styled-components';

import { LEFT_NAV_WIDTH } from 'dashboard/components-lib/NavAndPanelsOverlay/constants';
import LargeLeftPanel from 'dashboard/components-lib/NavAndPanelsOverlay/LargeLeftPanel';
import LeftNav, { LeftNavProps } from 'dashboard/components-lib/NavAndPanelsOverlay/LeftNav';
import { PanelContentScrollContainer } from 'dashboard/components-lib/NavAndPanelsOverlay/LeftPanelShared';
import SmallLeftPanel from 'dashboard/components-lib/NavAndPanelsOverlay/SmallLeftPanel';
import { NavItem } from 'dashboard/components-lib/NavAndPanelsOverlay/types';
import { Z_INDICES } from 'dashboard/styles/variables';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';

const PanelsOverlay = styled('div')<{ hasBottomPanel: boolean }>`
  position: absolute;
  inset: 0;
  left: ${LEFT_NAV_WIDTH}px;
  z-index: ${Z_INDICES.modal};
  // This overlay covers the full width and height of the content area, so pointer-events:none is
  // used to allow clicks to still pass through to any content not covered by panels underneath.
  // NOTE: All panels inside this overlay should have pointer-events:auto applied to allow
  // interacting with the panel content.
  pointer-events: none;

  ${({ hasBottomPanel }) =>
    hasBottomPanel &&
    css`
      display: grid;
      grid-template-rows: 1fr auto;
    `}
`;

const LeftPanelWrapper = styled('div')`
  height: 100%;
  min-height: 0;
  // Width needs to be 100% and take up the full PanelsOverlay since the LargeLeftPanel width is a percentage
  width: 100%;
`;

const HideWhenNotActive = styled('div')<{ isActive: boolean }>`
  display: ${({ isActive }) => (isActive ? 'contents' : 'none')};
`;

const BottomPanelWrapper = styled('div')`
  width: 100%;
  // Combats the pointer-events:none on the parent overlay to allow interacting with the panel content
  pointer-events: auto;
`;

interface NavAndPanelsOverlayContextValue {
  activeNavItem: NavItem | null;
  expandNavItem: (navItem: NavItem) => void;
  collapseNavItem: () => void;
  panelsOverlayRef: RefObject<HTMLDivElement> | null;
}

export const NavAndPanelsOverlayContext = createContext<NavAndPanelsOverlayContextValue>({
  activeNavItem: null,
  expandNavItem: () => {},
  collapseNavItem: () => {},
  panelsOverlayRef: null,
});

interface NavAndPanelsOverlayProps extends LeftNavProps {
  bottomPanel?: ReactNode;
}

/**
 * Renders a left nav that opens a panel for each nav item. The nav and panels are absolutely positioned
 * so will overlay any content below.
 *
 * To use, place `NavAndPanelsOverlay` in the the container where you want the nav and panels to overlay.
 * The container should have `position: relative` so the overlay can be absolutely positioned within it.
 * Optionally, apply `padding-left: ${LEFT_NAV_WIDTH}px` to the content underneath the overlay to
 * offset the left nav and prevent content from being hidden behind the left nav.
 */
const NavAndPanelsOverlay: FC<NavAndPanelsOverlayProps> = ({ bottomPanel, ...globalNavProps }) => {
  /**
   * TODO (fawn):
   * - hide the dashboard nav on load
   * - give focus to the panel when opened
   * - include the panel name in the page title so the history is more readable
   * - possibly do a replace instead of push in history when opening a panel
   * - specifically within FLE, make it so this overlay aligns with the bottom of the InquiryTemplateHeader border
   * - add tab/tablist/tabpanel roles
   */

  const enableLeftNav = useFeatureFlag('dashboard:fle-redesign-left-nav-and-panels');

  const { items } = globalNavProps;

  const { pathname } = useLocation();
  const initialActiveNavItem = useMemo(() => {
    return items
      .flatMap((section) => section)
      .find((item) => {
        return item.path && matchPath({ path: item.path.to, end: false }, pathname);
      });
  }, [items, pathname]);

  const [activeNavItem, setActiveNavItem] = useState<NavItem | null>(initialActiveNavItem || null);

  const smallPanels = items.flat().filter((item) => item.type.size === 'small');
  const largePanels = items.flat().filter((item) => item.type.size === 'large');

  const panelsOverlayRef = useRef<HTMLDivElement>(null);
  const rightColumnRef = useRef<HTMLDivElement>(null);

  if (!enableLeftNav) {
    return <></>;
  }

  return (
    <NavAndPanelsOverlayContext.Provider
      value={{
        activeNavItem,
        expandNavItem: (navItem) => setActiveNavItem(navItem),
        collapseNavItem: () => setActiveNavItem(null),
        panelsOverlayRef,
      }}
    >
      <LeftNav {...globalNavProps} />
      <PanelsOverlay hasBottomPanel={Boolean(bottomPanel)} ref={panelsOverlayRef}>
        {/* In order for the grid of `PanelsOverlay` to work (only applicable when there is a bottomPanel),
              there must always be a div present for the first row of the grid. This is the main purpose of
              LeftPanelWrapper. (Without it, there would be no left panel to fill the first row when there is
              no panel actively selected, which would cause the bottom panel to become the first row instead
              and align at the top of the screen instead of the bottom.) */}
        <LeftPanelWrapper>
          {/* `SmallLeftPanel` and `LargeLeftPanel` manage their own widths. We want to make it so that
                the width of all small panels and all large panels remains consistent when switching between
                panels of the same type.
                - Switching from large panel A to large panel B should maintain the same width for large panels.
                - Switching from small panel C to small panel D should maintain the same width for small panels.
                - The widths of large and small panels are independent of each other.
                To accomplish this, only one instance of a `SmallLeftPanel` and one instance of a `LargeLeftPanel`
                are rendered at any time, ensuring that each type maintains a single consistent width.*/}
          {/* Additionally, to avoid resetting the state of panel contents when switching the active panel,
                the contents are always mounted. Only the content of the active panel is displayed, while the
                contents of inactive panels are hidden.*/}
          {/* Additionally, to avoid resetting the scroll position of panel contents when switching the active panel,
                the contents are always wrapped in their own PanelContentScrollContainer. */}
          <HideWhenNotActive isActive={activeNavItem?.type.size === 'small'}>
            <SmallLeftPanel
              title={activeNavItem?.label || ''}
              basePath={activeNavItem?.path?.basePath}
            >
              {smallPanels.map((item) => (
                <HideWhenNotActive key={item.label} isActive={item.label === activeNavItem?.label}>
                  <PanelContentScrollContainer>{item.panelContent}</PanelContentScrollContainer>
                </HideWhenNotActive>
              ))}
            </SmallLeftPanel>
          </HideWhenNotActive>
          <HideWhenNotActive isActive={activeNavItem?.type.size === 'large'}>
            <LargeLeftPanel
              title={activeNavItem?.label || ''}
              basePath={activeNavItem?.path?.basePath}
              rightColumnRef={rightColumnRef}
            >
              {largePanels.map((item) => (
                <HideWhenNotActive key={item.label} isActive={item.label === activeNavItem?.label}>
                  <PanelContentScrollContainer>{item.panelContent}</PanelContentScrollContainer>
                  {item.type.size === 'large' && item.type.rightColumnId && (
                    <div
                      ref={rightColumnRef}
                      id={item.type.rightColumnId}
                      style={{ height: '100%' }}
                    />
                  )}
                </HideWhenNotActive>
              ))}
            </LargeLeftPanel>
          </HideWhenNotActive>
        </LeftPanelWrapper>
        {bottomPanel && <BottomPanelWrapper>{bottomPanel}</BottomPanelWrapper>}
      </PanelsOverlay>
    </NavAndPanelsOverlayContext.Provider>
  );
};

export default NavAndPanelsOverlay;
